import type { PayType } from '@/app/features/employee/emp-payroll';
import { PencilEdit02Icon } from '@/icons';
import { toast } from '@/lib/toast';
import { Button, Tooltip } from 'antd';
import { useRef, useState } from 'react';
import ManageGrade from './_components/SalaryGrade/ManageGrade';
import ManageSalaryStructure from './_components/SalaryGrade/ManageSalaryStructure';
import PayrollCalendar from './_components/SalaryGrade/PayrollCalendar';
import PayBasisSwitch from './_components/PayBasisSwitch';
import type { ConfigPanelHandle } from './_components/SalaryGrade/salary-grade.types';

type Panel = { label: string; ref: React.RefObject<ConfigPanelHandle | null> };

/** Joins panel names for a toast, e.g. "A, B and C". */
const joinNames = (names: string[]) =>
  names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0];

/**
 * Runs one step on every panel and keeps only the ones that reported a problem.
 * Panels answer with `null` when all is well, so anything else is a message.
 */
const collectProblems = async (
  panels: Panel[],
  step: (panel: Panel) => Promise<string | null> | undefined,
) => {
  const results = await Promise.all(
    panels.map(async (panel) => ({
      label: panel.label,
      problem: (await step(panel)) ?? null,
    })),
  );
  return results.filter(
    (result): result is { label: string; problem: string } =>
      Boolean(result.problem),
  );
};

/**
 * Salary Structure & Grade Management.
 *
 * The page owns a single edit mode. Every panel below stays read only until the
 * admin enters it, and one Save validates all panels, then sends all three
 * requests together. Panels that succeed keep their new values; a panel that
 * fails reports why and holds the page in edit mode so nothing is lost.
 */
export default function SalaryGrade() {
  /** One switch drives the calendar, the structure and the grades below it. */
  const [payType, setPayType] = useState<PayType>('MONTHLY');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const calendarRef = useRef<ConfigPanelHandle>(null);
  const structureRef = useRef<ConfigPanelHandle>(null);
  const gradeRef = useRef<ConfigPanelHandle>(null);

  const panels: Panel[] = [
    { label: 'Payroll calendar', ref: calendarRef },
    { label: 'Salary structure', ref: structureRef },
    { label: 'Grade management', ref: gradeRef },
  ];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Nothing is sent until every panel is valid, so a save can never leave
      // half a configuration behind because of a typo.
      const invalid = await collectProblems(panels, (panel) =>
        panel.ref.current?.validate(),
      );
      if (invalid.length) {
        invalid.forEach(({ label, problem }) =>
          toast('error', `${label}: ${problem}`),
        );
        return;
      }

      const failed = await collectProblems(panels, (panel) =>
        panel.ref.current?.save(),
      );
      const savedLabels = panels
        .map((panel) => panel.label)
        .filter((label) => !failed.some((item) => item.label === label));

      if (savedLabels.length) {
        toast(
          'success',
          failed.length
            ? `${joinNames(savedLabels)} updated successfully`
            : 'Payroll configuration updated successfully',
        );
      }
      failed.forEach(({ label, problem }) =>
        toast('error', `${label}: ${problem}`),
      );

      // Editing only ends once everything is safely stored.
      if (!failed.length) setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    panels.forEach((panel) => panel.ref.current?.reset());
    setIsEditing(false);
  };

  return (
    <div className="space-y-5">
      {/* What is being configured, and the single edit control */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Tooltip
          title={isEditing ? 'Save or cancel to switch' : ''}
          placement="bottom"
        >
          <div>
            <PayBasisSwitch
              value={payType}
              onChange={setPayType}
              disabled={isEditing}
            />
          </div>
        </Tooltip>

        <div className="flex items-center gap-3">
          {isEditing && (
            <Button
              size="large"
              disabled={isSaving}
              onClick={handleCancel}
              className="btn-primary-bordered min-w-[100px]"
            >
              Cancel
            </Button>
          )}
          <Button
            size="large"
            loading={isSaving}
            icon={!isEditing ? <PencilEdit02Icon size={18} /> : undefined}
            className="btn-primary h-11 font-semibold min-w-[150px]"
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
          >
            {isEditing ? 'Save Changes' : 'Edit Configuration'}
          </Button>
        </div>
      </div>

      {/*
        One row from 2xl up, structure + grade side by side on a laptop, and a
        plain stack on small screens. `items-start` keeps every card at its own
        height instead of stretching to the tallest one.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-5 2xl:grid-cols-12 gap-6 items-start">
        {/* key remounts every panel so switching pay type never mixes the two sets */}
        <div className="lg:col-span-5 2xl:col-span-3">
          <PayrollCalendar
            key={`calendar-${payType}`}
            ref={calendarRef}
            payType={payType}
            isEditing={isEditing}
          />
        </div>

        <div className="lg:col-span-2 2xl:col-span-4">
          <ManageSalaryStructure
            key={`structure-${payType}`}
            ref={structureRef}
            payType={payType}
            isEditing={isEditing}
          />
        </div>

        <div className="lg:col-span-3 2xl:col-span-5">
          <ManageGrade
            key={`grade-${payType}`}
            ref={gradeRef}
            payType={payType}
            isEditing={isEditing}
          />
        </div>
      </div>
    </div>
  );
}
